import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { acquireApiToken } from '@/auth/acquireApiToken';
import { Env, ENV_COLORS, ErrorBanner, Spinner, getAdminApiUrl } from '@/pages/Admin/shared';
import { useMarkets, useUnassignedBusinesses } from '@/pages/Marketplace/shared';
import type { MarketBusiness } from '@/pages/Marketplace/shared';
import OverrideAssignmentModal from './OverrideAssignmentModal';

const LS_KEY_ENV = 'marketplace.env';

export default function UnassignedBusinessesPage(): React.ReactElement {
  const { instance, accounts } = useMsal();
  const [env, setEnv] = useState<Env>(() => (localStorage.getItem(LS_KEY_ENV) as Env) || 'dev');
  const [accessToken, setAccessToken] = useState<string | null>(null);

  useEffect(() => { localStorage.setItem(LS_KEY_ENV, env); }, [env]);
  useEffect(() => {
    if (accounts.length === 0) return;
    acquireApiToken(instance, accounts[0]).then((accessToken) => ({ accessToken }))
      .then((r) => setAccessToken(r.accessToken))
      .catch(() => setAccessToken(null));
  }, [instance, accounts]);

  const apiUrl = getAdminApiUrl(env);
  const { items, cursor, loading, error, loadMore, refetch } = useUnassignedBusinesses(apiUrl, accessToken);
  const { data: markets } = useMarkets(apiUrl, accessToken);
  const [assigning, setAssigning] = useState<MarketBusiness | null>(null);
  const envColors = ENV_COLORS[env];

  return (
    <div className="p-6 text-gray-200 min-h-[calc(100vh-72px)]">
      <div className="flex items-center justify-between mb-4">
        <div>
          <Link to="/markets" className="text-xs text-gray-400 hover:text-white">← Markets</Link>
          <h1 className="text-xl font-semibold text-white mt-1">Unassigned Businesses</h1>
          <p className="text-sm text-gray-400">Businesses with no market (neither resolved nor overridden).</p>
        </div>
        <select
          value={env}
          onChange={(e) => setEnv(e.target.value as Env)}
          className={`text-xs px-2 py-1 rounded border ${envColors.pill}`}
        >
          <option value="dev">dev</option>
          <option value="staging">staging</option>
          <option value="prod">prod</option>
        </select>
      </div>

      {error && <ErrorBanner message={error} />}
      <div className="border border-gray-800 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-900 text-gray-400 text-xs uppercase">
            <tr>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">City</th>
              <th className="text-left px-3 py-2">State</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((b) => (
              <tr key={b.id} className="border-t border-gray-800">
                <td className="px-3 py-2">{b.name}</td>
                <td className="px-3 py-2 text-gray-400">{b.city ?? '—'}</td>
                <td className="px-3 py-2 text-gray-400">{b.state ?? '—'}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => setAssigning(b)} className="text-xs text-violet-300 hover:text-violet-200">
                    Assign market
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && <div className="p-3"><Spinner size="sm" /></div>}
        {cursor && !loading && (
          <button onClick={loadMore} className="w-full text-center text-xs text-gray-400 hover:text-white py-2 border-t border-gray-800">
            Load more
          </button>
        )}
      </div>

      {assigning && markets && (
        <OverrideAssignmentModal
          business={assigning}
          markets={markets}
          apiUrl={apiUrl}
          accessToken={accessToken}
          onClose={() => setAssigning(null)}
          onSaved={refetch}
        />
      )}
    </div>
  );
}
